import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { leadApi } from '../services/api';
import { LoadingSpinner } from '../components/common';
import { ApiKeyManager } from '../components/settings';
import { parseCSV, toCSV, downloadFile } from '../utils/helpers';

const Settings = () => {
  const { settings, updateSettings, showSnackbar } = useApp();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    email: '',
    timezone: 'America/Chicago',
    defaultMonthlyRate: 40,
    defaultBillingDay: 1,
    mrrGoal: 2000,
    clientGoal: 50,
    autoReminders: true,
    darkMode: false,
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        businessName: settings.businessName || '',
        email: settings.email || '',
        timezone: settings.timezone || 'America/Chicago',
        defaultMonthlyRate: settings.defaultMonthlyRate || 40,
        defaultBillingDay: settings.defaultBillingDay || 1,
        mrrGoal: settings.mrrGoal || 2000,
        clientGoal: settings.clientGoal || 50,
        autoReminders: settings.autoReminders !== false,
        darkMode: settings.darkMode || false,
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateSettings(formData);
    } catch (error) {
      // Error handled in context
    } finally {
      setLoading(false);
    }
  };

  const handleExportLeads = async () => {
    try {
      const response = await leadApi.exportLeads({});
      const csv = toCSV(response.data, [
        { field: 'instagramHandle', label: 'Instagram Handle' },
        { field: 'businessName', label: 'Business Name' },
        { field: 'ownerName', label: 'Owner Name' },
        { field: 'email', label: 'Email' },
        { field: 'phone', label: 'Phone' },
        { field: 'niche', label: 'Niche' },
        { field: 'city', label: 'City' },
        { field: 'stateProvince', label: 'State' },
        { field: 'country', label: 'Country' },
        { field: 'followerCount', label: 'Followers' },
        { field: 'stage', label: 'Stage' },
        { field: 'source', label: 'Source' },
        { field: 'notes', label: 'Notes' },
      ]);
      downloadFile(csv, `leads-backup-${new Date().toISOString().split('T')[0]}.csv`);
      showSnackbar('Leads exported successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to export leads', 'error');
    }
  };

  const handleImportLeads = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const leads = parseCSV(text);

      if (leads.length === 0) {
        showSnackbar('No valid leads found in file', 'error');
        return;
      }

      const response = await leadApi.importLeads({ leads });
      showSnackbar(
        `Import complete: ${response.data.success} added, ${response.data.failed} failed`,
        response.data.failed > 0 ? 'warning' : 'success'
      );
    } catch (error) {
      showSnackbar('Failed to import leads', 'error');
    }

    // Reset file input
    event.target.value = '';
  };

  if (!settings) {
    return <LoadingSpinner message="Loading settings..." />;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Profile Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Profile
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  select
                  label="Timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  SelectProps={{ native: true }}
                >
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </TextField>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Goals */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Goals
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="MRR Goal"
                  name="mrrGoal"
                  type="number"
                  value={formData.mrrGoal}
                  onChange={handleChange}
                  InputProps={{ startAdornment: '$' }}
                  helperText="Your target monthly recurring revenue"
                />
                <TextField
                  fullWidth
                  label="Client Goal"
                  name="clientGoal"
                  type="number"
                  value={formData.clientGoal}
                  onChange={handleChange}
                  helperText="Your target number of active clients"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Defaults */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Billing Defaults
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Default Monthly Rate"
                  name="defaultMonthlyRate"
                  type="number"
                  value={formData.defaultMonthlyRate}
                  onChange={handleChange}
                  InputProps={{ startAdornment: '$' }}
                />
                <TextField
                  fullWidth
                  label="Default Billing Day"
                  name="defaultBillingDay"
                  type="number"
                  value={formData.defaultBillingDay}
                  onChange={handleChange}
                  inputProps={{ min: 1, max: 28 }}
                  helperText="Day of month (1-28)"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Preferences */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Preferences
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.autoReminders}
                      onChange={handleChange}
                      name="autoReminders"
                    />
                  }
                  label="Auto-set follow-up reminders"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.darkMode}
                      onChange={handleChange}
                      name="darkMode"
                    />
                  }
                  label="Dark mode (coming soon)"
                  disabled
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Management */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Data Management
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Export your data regularly for backup. Import leads from a CSV file with columns:
                instagram_handle, business_name, owner_name, email, phone, niche, city, state_province
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportLeads}
                >
                  Export All Leads
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  component="label"
                >
                  Import Leads
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={handleImportLeads}
                  />
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* API Keys */}
        <Grid item xs={12}>
          <ApiKeyManager />
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;
